import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ingestionService from '../services/ingestionService';

/**
 * Hook for handling ingestion throughout the app
 */
export function useIngestion() {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  /**
   * Handle file uploads with progress tracking
   */
  const uploadFiles = useCallback(async (files, onProgress) => {
    if (!user?.id) {
      setUploadError('User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    setIsUploading(true);
    setUploadError(null);

    const results = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        console.log(`📤 [useIngestion] Uploading file ${i + 1}/${files.length}: ${file.name}`);
        
        // Report start
        if (onProgress) {
          onProgress(i, 0);
        }

        // Upload the file using the correct method: ingestUpload (not uploadFile)
        try {
          const item = await ingestionService.ingestUpload(user.id, file, (progress) => {
            if (onProgress) {
              onProgress(i, progress);
            }
          });
          
          console.log(`✅ [useIngestion] Upload successful:`, item);
          
          // Report completion
          if (onProgress) {
            onProgress(i, 100);
          }
          
          results.push({ 
            success: true, 
            item,
            filename: file.name 
          });
        } catch (uploadError) {
          console.error(`❌ [useIngestion] Failed to upload ${file.name}:`, uploadError);
          setUploadError(uploadError.message);
          results.push({ 
            success: false, 
            error: uploadError.message, 
            filename: file.name 
          });
        }
      }

      setIsUploading(false);

      return {
        success: results.every(r => r.success),
        results
      };
    } catch (error) {
      console.error('Upload exception:', error);
      setUploadError(error.message);
      setIsUploading(false);
      
      return {
        success: false,
        error: error.message
      };
    }
  }, [user]);

  /**
   * Track a web clip from Chrome extension
   */
  const trackWebClip = useCallback(async (itemId, meta = {}) => {
    if (!user?.id) {
      return { error: 'User not authenticated' };
    }

    return await ingestionService.trackIngestion(user.id, 'web_clip', itemId, meta);
  }, [user]);

  /**
   * Track a note creation
   */
  const trackNote = useCallback(async (itemId, meta = {}) => {
    if (!user?.id) {
      return { error: 'User not authenticated' };
    }

    return await ingestionService.trackIngestion(user.id, 'note', itemId, meta);
  }, [user]);

  /**
   * Track a link save
   */
  const trackLink = useCallback(async (itemId, meta = {}) => {
    if (!user?.id) {
      return { error: 'User not authenticated' };
    }

    return await ingestionService.trackIngestion(user.id, 'link', itemId, meta);
  }, [user]);

  /**
   * Get recent ingestions (unified feed)
   */
  const getRecentIngestions = useCallback(async (options = {}) => {
    if (!user?.id) {
      return { error: 'User not authenticated' };
    }

    const { limit = 50, offset = 0, sourceType = null } = options;
    
    return await ingestionService.getRecentIngestions(limit, offset, sourceType);
  }, [user]);

  return {
    isUploading,
    uploadError,
    uploadFiles,
    trackWebClip,
    trackNote,
    trackLink,
    getRecentIngestions
  };
}

